/**
 * usePermissions()
 *
 * Single hook for all access decisions.  Assembles a UserPermissions snapshot
 * from AuthContext + CompanyContext, then exposes:
 *
 *   can(req)        — Route/operation guard (super-admin always passes).
 *   moduleActive(m) — Raw company licence check (NO super-admin bypass).
 *                     Use this for sidebar visibility so super-admin sees
 *                     exactly what the client company sees.
 *   loadingModules  — True while company data is being fetched.
 *                     Gate UI on this to prevent flash-of-content.
 *
 * ─── Phase 76.2.D — leaf-gate cutover ──────────────────────────────────────
 * `can()` is now an OR-merge between the legacy decision and the Phase 76
 * authoritative decision. The legacy path stays as a permissive fallback for
 * the grace window where the Phase 76 cache hasn't hydrated yet, or for
 * permission keys the v76 seed doesn't model (plan tier, requireCompanyAdmin).
 * All leaf action gates that call `usePermissions().can(...)` automatically
 * benefit from this without per-file edits.
 *
 * The Phase 76 path checks:
 *   • Module subscription via `hasModuleInCompany(activeCompanyId, module)`
 *     against `company_module_subscriptions_v76` (authoritative).
 *   • Per-permission grant via `hasPermissionInCompany(activeCompanyId, key)`
 *     against `get_my_permissions_for_company()` (authoritative).
 *   • Super-admin bypass via `useIsSuperAdmin()` (server-pinned RPC).
 *
 * Legacy semantics are kept identical for: plan tier (free/premium/admin)
 * and `requireCompanyAdmin`, since Phase 76 does not yet model those axes.
 *
 * ─── RBAC decision flow (post-cutover) ──────────────────────────────────────
 *  can():
 *    1. isSuperAdmin → always true  (route bypass)
 *    2. meetsPlanTier                                      (legacy)
 *    3. requireCompanyAdmin? → companyRole === 'admin'     (legacy)
 *    4. module specified → Phase 76 (auth) || legacy companyModuleActive+level
 *
 *  moduleActive():
 *    → checks activeModules.includes(module)  ONLY — no super-admin bypass
 *      (legacy behaviour preserved for sidebar visibility parity).
 */

import { useMemo }    from 'react';
import { useAuth }    from '@/features/auth';
import { useCompany } from '@/context/CompanyContext';
import { usePhase76Auth } from '@/lib/auth';
import {
  can as canCheck,
  isCompanyAdmin as checkIsCompanyAdmin,
  canAccessPremium,
  companyModuleActive,
  meetsPlanTier,
  type UserPermissions,
  type PermissionRequest,
  type AppModule,
  type AccessLevel,
} from '@/lib/rbac';

// ── Phase 76 permission-key mapping (mirrors RequirePermission) ──────────────
//
// Maps a legacy (module, level) pair into the canonical Phase 76 permission
// key seeded server-side. Keep in sync with the Phase76Permission union in
// `src/lib/auth/phase76Client.ts` and the seed migration.
function permissionKeyFor(module: string, level: AccessLevel): string {
  const write = level === 'editor' || level === 'admin';
  if (module === 'hr') {
    return write ? 'hr.employee.write' : 'hr.employee.read.own';
  }
  return write ? `${module}.write` : `${module}.read`;
}

export function usePermissions() {
  const { profile }  = useAuth();
  const {
    activeCompanyRole,
    activeModulePermissions,
    activeModules,
    loadingCompanies,
  } = useCompany();

  // ── Phase 76 primary source ───────────────────────────────────────────────
  const {
    hydrated:              phase76Hydrated,
    isSuperAdmin:          phase76IsSuperAdmin,
    activeCompanyId:       phase76ActiveCompanyId,
    permissionsByCompany:  phase76PermsByCompany,
    hasModuleInCompany:    phase76HasModuleInCompany,
    hasPermissionInCompany: phase76HasPermissionInCompany,
  } = usePhase76Auth();

  const phase76CacheReadyForActive =
    phase76Hydrated &&
    !!phase76ActiveCompanyId &&
    !!phase76PermsByCompany[phase76ActiveCompanyId];

  // ── Full permission snapshot ────────────────────────────────────────────────
  // `isSuperAdmin` here OR-merges the legacy plan_tier check with the Phase 76
  // RPC-derived flag, so super-admin status survives even if the legacy
  // profile column drifts during the cutover.
  const permissions = useMemo<UserPermissions>(() => ({
    planTier:          profile?.plan_tier ?? 'free',
    companyRole:       activeCompanyRole,
    modulePermissions: activeModulePermissions,
    isSuperAdmin:      phase76IsSuperAdmin || profile?.plan_tier === 'admin',
    activeModules,
  }), [
    profile?.plan_tier,
    activeCompanyRole,
    activeModulePermissions,
    activeModules,
    phase76IsSuperAdmin,
  ]);

  // ── Route/operation guard — Phase 76 OR legacy ───────────────────────────
  const can = useMemo(
    () => (req: PermissionRequest): boolean => {
      // 1. Super-admin always passes.
      if (permissions.isSuperAdmin) return true;

      // 2. Plan tier / requireCompanyAdmin remain LEGACY-ONLY axes — they are
      //    not modelled in the Phase 76 seed, so we keep the legacy semantics.
      if (req.minPlan && !meetsPlanTier(permissions.planTier, req.minPlan)) {
        return false;
      }
      if (req.requireCompanyAdmin && permissions.companyRole !== 'admin') {
        return false;
      }

      // 3. Module + level → OR-merge between Phase 76 (authoritative) and
      //    legacy (`canCheck`). Phase 76 is consulted ONLY when its cache is
      //    populated for the active company, otherwise we trust the legacy
      //    decision for the grace window.
      if (req.module) {
        const moduleStr = req.module as string;
        const level     = (req.level ?? 'viewer') as AccessLevel;
        const phase76Decision = phase76CacheReadyForActive
          ? (
              phase76HasModuleInCompany(phase76ActiveCompanyId!, moduleStr) &&
              phase76HasPermissionInCompany(
                phase76ActiveCompanyId!,
                permissionKeyFor(moduleStr, level),
              )
            )
          : false;
        const legacyDecision = canCheck(permissions, req);
        return phase76Decision || legacyDecision;
      }

      // 4. No module → only the plan/admin axes matter; if we got here both
      //    passed, grant access.
      return true;
    },
    [
      permissions,
      phase76CacheReadyForActive,
      phase76ActiveCompanyId,
      phase76HasModuleInCompany,
      phase76HasPermissionInCompany,
    ],
  );

  // ── ★ Raw company module check (NO super-admin bypass) ────────────────────
  //
  // Use this in the sidebar and any "what does this company have?" UI.
  // Super-admin will see the same as regular users for the active company.
  // This means: if cashflow is off for the company, SA's sidebar hides it too.
  //
  // Phase 76.2.D: OR-merge with the new subscription cache once hydrated, so
  // the sidebar surfaces newly-toggled modules without waiting for the legacy
  // CompanyContext to refresh.
  const moduleActive = useMemo(
    () => (module: AppModule) => {
      const legacy = companyModuleActive(activeModules, module);
      if (phase76CacheReadyForActive) {
        return legacy || phase76HasModuleInCompany(phase76ActiveCompanyId!, module);
      }
      return legacy;
    },
    [
      activeModules,
      phase76CacheReadyForActive,
      phase76ActiveCompanyId,
      phase76HasModuleInCompany,
    ],
  );

  return {
    permissions,
    can,

    // ★ Raw company module visibility (no SA bypass)
    moduleActive,

    // True while EITHER source is still hydrating — gate guards on this.
    loadingModules: loadingCompanies || !phase76Hydrated,

    // ── Named booleans ────────────────────────────────────────────────────────
    isPremium:      canAccessPremium(permissions),
    isSuperAdmin:   permissions.isSuperAdmin,
    isCompanyAdmin: checkIsCompanyAdmin(permissions),

    // ── Module shortcuts (include company gate AND user-level check) ──────────
    // Used by RequirePermission / route guards — SA still bypasses these.
    // These delegate to `can()` above so they automatically inherit the
    // Phase 76 OR-merge semantics.
    canViewCashflow:  can({ module: 'cashflow',  level: 'viewer' }),
    canEditCashflow:  can({ module: 'cashflow',  level: 'editor' }),
    canViewHR:        can({ module: 'hr',        level: 'viewer' }),
    canEditHR:        can({ module: 'hr',        level: 'editor' }),
    canViewInventory: can({ module: 'inventory', level: 'viewer' }),
  };
}
